import { colors as themeColors } from '../utils/theme';
import React, { useEffect } from "react";
import {
  Container, Paper, Box, Grid, Typography,
  Checkbox, FormControlLabel, Alert, Tooltip, IconButton
} from "@mui/material";
import Button from "@mui/material/Button";
import { FiLoader } from "react-icons/fi";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { Close as CloseIcon } from "@mui/icons-material";
import { DoneAll as DoneAllIcon } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InputMask from "react-input-mask";
import FloatingLabelInput from "../components/FloatingLabelInput";
import DateInput from "../components/DateInput";
import Dropdown from "../components/Dropdown";
import SearchableDropdown from "../components/SearchableDropdown";

const requiredAsteriskStyle = { '& .MuiFormLabel-asterisk': { color: 'red' } };

const UserDetailsStep = ({
  mode,
  employeeName, setEmployeeName,
  mobileNumber,
  emailId,
  Designation, setDesignation,
  userName,
  password, setPassword,
  DateOfBirth, setDateOfBirth,
  joiningDate, setJoiningDate,
  days, setDays,
  enableDays, setEnableDays,
  category,
  IsActive, setIsActive,
  selectedTeamLeaders, setSelectedTeamLeaders,
  errors,
  updateMessage, updateStatus,
  showPassword, setShowPassword,
  isSubmitting,
  isPasswordError,
  usernameError, usernameValid, usernameCheckLoading,
  mobileError, mobileValid, mobileCheckLoading,
  emailTouched, emailInvalid, emailDomainWarning,
  showEmailSuggestions, setShowEmailSuggestions,
  EMAIL_SUGGESTIONS,
  handleSubmit,
  handleCancel,
  handleCategoryChange,
  handleMobileChange,
  handleUsernameChange,
  handleEmailChange,
  handleEmailBlur,
  handleEmailSuggestionClick,
  checkMobileNumber,
  checkUsername,
  fetchTeamLeaders,
  isFormComplete,
  getFutureDate,
  categoryOptions,
  navigate,
  toProperCase,
  // ─── Signature props ──────────────────────────────────────
  signaturePreview,
  signatureFileRef,
  isSignatureUploading,
  onSignatureFileChange,
  onSignatureRemove,
}) => {
  // ──────────────── Alt+S (Save) & Alt+C (Cancel) shortcuts ────────────────
  // Attached to document in the capture phase so it fires regardless of which
  // field/dropdown currently has focus, including MUI popper/portal content.
  // Uses e.code (physical key position) instead of e.key so this also works on
  // macOS, where Option+letter often produces an accented/dead-key character
  // via e.key (e.g. Option+C → "ç") instead of the plain letter.
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (!e.altKey) return;

      if (e.code === "KeyS") {
        e.preventDefault();
        if (!isSubmitting && isFormComplete()) {
          handleSubmit(e);
        }
      } else if (e.code === "KeyC") {
        e.preventDefault();
        handleCancel();
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
  });

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>

        {/* Header */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.6, mt: -1.5 }}>
          <IconButton
            onClick={handleCancel}
            sx={{ position: 'absolute', left: 0, display: { xs: 'flex', md: 'none' }, color: 'text.secondary', p: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
            {mode === 'edit' ? 'Edit User' : 'Add New User'}
          </Typography>
        </Box>

        {updateMessage && (
          <Alert severity={updateStatus} sx={{ mb: 2 }}>{updateMessage}</Alert>
        )}

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Grid container spacing={1.5} direction="column">

            {/* Employee Name */}
            <Grid item xs={12}>
              <FloatingLabelInput
                label="Employee Name" name="EmployeeName" type="text" required
                value={toProperCase(employeeName)}
                onChange={(e) => setEmployeeName(toProperCase(e.target.value))}
                error={Boolean(errors.employeeName)}
                helperText={errors.employeeName}
                InputLabelProps={{ sx: requiredAsteriskStyle }}
                sx={{
                  '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                  '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' },
                  "& .MuiFormLabel-asterisk": { color: "gray" },
                }}
              />
            </Grid>

            {/* Mobile No */}
            <Grid item xs={12}>
              <Tooltip
                title={mobileCheckLoading ? "Checking mobile number..." : ""}
                arrow
                disableHoverListener={!mobileCheckLoading}
                slotProps={{ tooltip: { sx: { fontSize: "0.9rem" } } }}
              >
                <Box>
                  <InputMask
                    mask="9999999999" maskChar=""
                    value={mobileNumber}
                    onChange={handleMobileChange}
                    onBlur={() => checkMobileNumber(mobileNumber)}
                  >
                    {(inputProps) => (
                      <FloatingLabelInput
                        {...inputProps} required label="Mobile No" name="MobileNo" type="text"
                        error={Boolean(errors.mobileNumber) || Boolean(mobileError)}
                        helperText={errors.mobileNumber || mobileError}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: 38, display: 'flex', alignItems: 'center',
                            '& fieldset': { borderColor: mobileError ? themeColors.error.main : undefined, borderWidth: mobileError ? 2 : 1 },
                            '&:hover fieldset': { borderColor: mobileError ? themeColors.error.main : undefined },
                            '&.Mui-focused fieldset': { borderColor: mobileError ? themeColors.error.main : undefined },
                          },
                        }}
                        InputProps={{
                          endAdornment: mobileCheckLoading ? (
                            <FiLoader className="animate-spin" size={20} style={{ color: '#3b82f6' }} />
                          ) : mobileError ? (
                            <CloseIcon sx={{ color: themeColors.error.main, fontSize: 24 }} />
                          ) : mobileValid ? (
                            <DoneAllIcon sx={{ color: '#25D366', fontSize: 24 }} />
                          ) : null,
                        }}
                      />
                    )}
                  </InputMask>
                </Box>
              </Tooltip>
            </Grid>

            {/* Email */}
            <Grid item xs={12} mt={mobileError ? 2.5 : 0}>
              <Tooltip
                title={
                  emailTouched && emailDomainWarning
                    ? `Looks like spelling mistake. Did you mean ${emailDomainWarning}?`
                    : emailTouched && emailInvalid ? "Invalid email format" : ""
                }
                arrow
                disableHoverListener={!(emailTouched && (emailInvalid || emailDomainWarning))}
                slotProps={{ tooltip: { sx: { fontSize: "0.9rem" } } }}
              >
                <Box sx={{ position: "relative" }} component="form" autoComplete="off">
                  <FloatingLabelInput
                    required label="Email ID" name="emailId" type="email"
                    value={emailId} spellCheck="false" autoComplete="off"
                    inputProps={{ autoComplete: "off", form: { autoComplete: "off" } }}
                    onChange={handleEmailChange}
                    onFocus={() => setShowEmailSuggestions(true)}
                    onBlur={handleEmailBlur}
                    error={Boolean(errors.emailId) || emailInvalid}
                    helperText={errors.emailId}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 38, display: 'flex', alignItems: 'center',
                        "& fieldset": {
                          borderColor: emailTouched && emailDomainWarning ? "#ffc400" : emailTouched && emailInvalid ? themeColors.error.main : undefined,
                          borderWidth: emailTouched && (emailInvalid || emailDomainWarning) ? 2 : 1,
                        },
                      },
                    }}
                  />
                  {showEmailSuggestions && (
                    <Box sx={{ position: "absolute", top: "calc(100% + 8px)", left: 12, bgcolor: themeColors.neutral.surface, borderRadius: 2, px: 1, py: 0.5, zIndex: 20, boxShadow: "0px 6px 18px rgba(0,0,0,0.15)", minWidth: 260 }}>
                      <Box sx={{ position: "absolute", top: -6, left: 16, width: 12, height: 12, bgcolor: themeColors.neutral.surface, transform: "rotate(45deg)" }} />
                      {EMAIL_SUGGESTIONS
                        .filter(item => !emailId || item.toLowerCase().includes(emailId.toLowerCase()))
                        .map(item => (
                          <Box key={item}
                            sx={{ px: 1.5, py: 0.75, fontSize: 13, borderRadius: 1, cursor: "pointer", "&:hover": { backgroundColor: "#f0f4ff" } }}
                            onMouseDown={() => handleEmailSuggestionClick(item)}
                          >
                            {item}
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </Grid>

            {/* Designation */}
            <Grid item xs={12}>
              <FloatingLabelInput
                required label="Designation" name="Designation" type="text"
                value={toProperCase(Designation)}
                onChange={(e) => setDesignation(toProperCase(e.target.value))}
                error={Boolean(errors.Designation)}
                helperText={errors.Designation}
                sx={{
                  '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                  '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' },
                  "& .MuiFormLabel-asterisk": { color: "gray" },
                }}
              />
            </Grid>

            {/* Username */}
            <Grid item xs={12}>
              <Tooltip
                title={usernameCheckLoading ? "Checking availability..." : ""}
                arrow
                disableHoverListener={!usernameCheckLoading}
                slotProps={{ tooltip: { sx: { fontSize: "0.9rem" } } }}
              >
                <Box>
                  <FloatingLabelInput
                    required label="User Name" name="UserName" type="text"
                    value={userName}
                    onChange={handleUsernameChange}
                    onBlur={() => checkUsername(userName)}
                    error={Boolean(errors.userName) || Boolean(usernameError)}
                    helperText={errors.userName || usernameError}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 38, display: 'flex', alignItems: 'center',
                        '& fieldset': { borderColor: usernameError ? themeColors.error.main : undefined, borderWidth: usernameError ? 2 : 1 },
                        '&:hover fieldset': { borderColor: usernameError ? themeColors.error.main : undefined },
                        '&.Mui-focused fieldset': { borderColor: usernameError ? themeColors.error.main : undefined },
                      },
                    }}
                    InputProps={{
                      endAdornment: usernameCheckLoading ? (
                        <FiLoader className="animate-spin" size={20} style={{ color: '#3b82f6' }} />
                      ) : usernameError ? (
                        <CloseIcon sx={{ color: themeColors.error.main, fontSize: 24 }} />
                      ) : usernameValid ? (
                        <DoneAllIcon sx={{ color: '#25D366', fontSize: 24 }} />
                      ) : null,
                    }}
                  />
                </Box>
              </Tooltip>
            </Grid>

            {/* Password */}
            <Grid item size={{ xs: 12 }} mt={usernameError ? 2.5 : 0}>
              <FloatingLabelInput
                required label="Password" name="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={Boolean(errors.password)}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <Box sx={{ cursor: "pointer" }} onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
                    </Box>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                  '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' },
                  "& .MuiFormLabel-asterisk": { color: "gray" },
                }}
              />
            </Grid>

            {/* DOB + Joining Date */}
            <Grid container justifyContent="space-between" mt={isPasswordError ? 2.5 : 0}>
              <Grid item size={{ xs: 6 }}>
                <DateInput
                  fullWidth margin="none" required
                  label={<>Date of Birth <span style={{ color: themeColors.error.dark, fontWeight: 500 }}>*</span></>}
                  name="DateOfBirth" value={DateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  error={Boolean(errors.DateOfBirth)}
                  helperText={errors.DateOfBirth}
                  sx={{ height: 38 }}
                />
              </Grid>
              <Grid item size={{ xs: 6 }}>
                <DateInput
                  fullWidth margin="none" required
                  label={<>Joining Date <span style={{ color: themeColors.error.dark, fontWeight: 500 }}>*</span></>}
                  name="JoiningDate" value={joiningDate}
                  onChange={(e) => setJoiningDate(e.target.value)}
                  error={Boolean(errors.joiningDate)}
                  helperText={errors.joiningDate}
                  sx={{ height: 38 }}
                />
              </Grid>
            </Grid>

            {/* Category */}
            <Grid item xs={12}>
              <Dropdown
                id="category" name="category" required
                label={<>Category</>}
                valueKey="id" labelKey="name"
                value={category}
                options={categoryOptions}
                onChange={handleCategoryChange}
                InputLabelProps={{ sx: requiredAsteriskStyle }}
                fullWidth disabled={false}
              />
              {errors.category && (
                <Typography variant="caption" color="error">{errors.category}</Typography>
              )}
            </Grid>

            {/* Assign to Members */}
            <Grid item xs={12}>
              <SearchableDropdown
                label="Assign to Members"
                value={selectedTeamLeaders}
                onChange={setSelectedTeamLeaders}
                fetchOptions={fetchTeamLeaders}
                valueKey="UserId" labelKey="EmployeeName"
                placeholder="Search and select multiple members..."
                error={Boolean(errors.teamLeaders)}
                helperText={errors.teamLeaders || "You can assign multiple members"}
              />
            </Grid>

            {/* Active checkbox */}
            <Grid item xs={12} mt={-2} mb={-1.5}>
              <FormControlLabel
                label="Active"
                control={
                  <Checkbox
                    checked={IsActive} color="primary"
                    onChange={(e) => {
                      const newVal = e.target.checked;
                      setIsActive(newVal);
                      if (newVal) setEnableDays(false);
                    }}
                  />
                }
              />
            </Grid>

            {/* Freeze section — edit mode only */}
            {mode === "edit" && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    label="Freeze"
                    control={
                      <Checkbox
                        color="primary" checked={enableDays}
                        onChange={(e) => {
                          const newVal = e.target.checked;
                          setEnableDays(newVal);
                          if (newVal) setIsActive(false);
                        }}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FloatingLabelInput
                    label="No. Days" name="days" type="number"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    disabled={!enableDays}
                    sx={{
                      '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                      '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' },
                      "& .MuiFormLabel-asterisk": { color: "inherit" },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FloatingLabelInput
                    label="Future Date"
                    value={days ? getFutureDate(days) : ""}
                    disabled
                    sx={{
                      '& .MuiOutlinedInput-root': { height: 38, display: 'flex', alignItems: 'center' },
                      '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' },
                      "& .MuiFormLabel-asterisk": { color: "inherit" },
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Authorized Signature — commented out, moved to CompanyMaster */}
            {/* <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Authorized Signature
              </Typography>
              <Box
                sx={{
                  border: '1.5px dashed #bdbdbd',
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  background: '#fafafa',
                  minHeight: 90,
                  cursor: signaturePreview ? 'default' : 'pointer',
                }}
                onClick={() => { if (!signaturePreview) signatureFileRef.current?.click(); }}
              >
                {signaturePreview ? (
                  <>
                    <Box
                      component="img"
                      src={signaturePreview}
                      alt="Signature Preview"
                      sx={{ maxHeight: 80, maxWidth: '100%', objectFit: 'contain', borderRadius: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => { e.stopPropagation(); signatureFileRef.current?.click(); }}
                      >
                        Change
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); onSignatureRemove(); }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </>
                ) : isSignatureUploading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FiLoader className="animate-spin" size={18} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Uploading...</Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Click to upload signature image
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); signatureFileRef.current?.click(); }}
                    >
                      Upload Signature
                    </Button>
                  </>
                )}
                <input
                  ref={signatureFileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={onSignatureFileChange}
                />
              </Box>
            </Grid> */}

            {/* Submit */}
            <Grid item xs={12} mb={-0.5}>
              <Button
                fullWidth variant="contained" color="primary"
                type="submit"
                disabled={isSubmitting || !isFormComplete()}
              >
                {isSubmitting ? (
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <FiLoader className="animate-spin" style={{ marginRight: 8 }} />
                    Processing...
                  </Box>
                ) : mode === "edit" ? "Update And Next" : "Save And Next"}
              </Button>
            </Grid>

            {/* Cancel */}
            <Grid item xs={12} mb={-0.5}>
              <Button fullWidth variant="contained" color="error" onClick={handleCancel}>
                Close
              </Button>
            </Grid>

          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default UserDetailsStep;